
import { action, observable } from 'mobx';
import { WaterwayDesignToolsController, StructureController, DesignElementController } from 'cd-eft/services/eftSetApi';
import stores from 'cd-eft/models/Stores';
import MessageCenter from 'cd-eft/components/common/MessageCenter/MessageCenterVM';
import { DesignSurfaceRequest } from 'cd-eft/_codegen_/openapi-generator/eft-set';
import { DesignContainerVM } from 'cd-eft/components/Design/DesignContainer/DesignContainerVM';
import { EngineeringMapService } from 'cd-eft/framework/mapServices/EngineeringMapServices';
import MapView from '@arcgis/core/views/MapView';
import { GroupLayer } from 'cd-eft/utils/esriTypes';

const { designStore, waterwayDesignStore: waterwayStore } = stores.AppStore;
export interface IWaterwaySummaryStation {
  select: boolean;
  name: string;
  surface: string;
  alignment: string;
  hydrology: number;
  length: number;
  reachCount: number;
  maxTopWidth: number;
  lastUpdatedDate: string;
  alignedDesignElementId: number;
  waterwayType: string;
  channelType: string;
}

interface INotification {
    surface: number,
    alignment: number,
    designControl: number,
    peakFlow: number,
    reaches: number
}

export class SummaryVM {
  private mapView: MapView | undefined;

  constructor(private onClickEdit:(id?)=>void) {
    if (EngineeringMapService.instance?.mapView) {
      this.mapView = EngineeringMapService.instance.mapView;
    }
    waterwayStore.uiState = "";
  }

  @observable waterwaySummaryStationList: IWaterwaySummaryStation[] = [];
  @observable notificationDictionary: {[key:number]:INotification | null} = {};

  @observable headerTitle = "";
  @observable onClickInformation = false;
  @observable waterwayName = "";
  @observable waterWayWizardStatus: number = 1;  // 1: default, 2: success, 3:error
  @observable allWDTProjectList: any[] = [];
  @observable showIcon: boolean = false; 

  @action.bound onClickInformationToggle = (title) => {
    this.headerTitle = title
    this.onClickInformation = !this.onClickInformation
  }

  @action.bound async getWaterwaySummary() {
    waterwayStore.summaryWaterwayTypes = [];
    waterwayStore.currentWaterwayDesignElementTypeId = 0;
    if (!stores.AppStore.designStore.projectsStore.currentSessionId) {
      return;
    }
    try {
      await this.getAllAlignmentsStatus();
      const response = await WaterwayDesignToolsController.getWaterwayDesignSummaryBySessionUuidUsingGET(stores.AppStore.designStore.projectsStore.currentSessionId);
      if (response && response.data) {
        const data = response.data;
        this.waterwaySummaryStationList = data.map((e) => {
          waterwayStore.summaryWaterwayTypes.push({elementId: e.alignedElementDesignParamsId, type: e.waterwayType});
          const newSum: IWaterwaySummaryStation = {
            select: false,
            name: e.name ? e.name : "",
            surface: e.surface ? e.surface : "",
            alignment: e.alignmentName ? e.alignmentName : "",
            hydrology: e.hydrology ? e.hydrology : 0,
            length: e.length ? e.length : 0,
            reachCount: e.reachCount ? e.reachCount : 0,
            maxTopWidth: e.maxTopWidth ? e.maxTopWidth : 0,
            lastUpdatedDate: e.lastUpdate ? e.lastUpdate.toString() : "",
            alignedDesignElementId: e.alignedElementDesignParamsId !== undefined ? e.alignedElementDesignParamsId : -1, // -1 means undefined  self-defined from FE due to typescript type integration
            channelType: e.channelType? e.channelType : "",
            waterwayType: e.waterwayType? e.waterwayType : ""
          }
          return newSum
        })
      }
    }
    catch (err) {
      console.error(err)
    } 
  }

  @action.bound async getNotifications() {
    if (!stores.AppStore.designStore.projectsStore.currentSessionId) {
      return;
    }
    try{
      this.notificationDictionary = {};
      const response = await WaterwayDesignToolsController.getAlignedElementDesignParamsIdsAndNotificationsBySessionUuidUsingGET(stores.AppStore.designStore.projectsStore.currentSessionId);
      if(response && response.data && response.data.alignedElementIdsAndNotificationsDtoList){
        const data = response.data.alignedElementIdsAndNotificationsDtoList;
        this.allWDTProjectList.push(...data);
        data.forEach((e)=>{
          const newNotification: INotification ={
            surface:e.surfaceChange!,
            alignment: e.alignmentChange!,
            designControl: e.alignedFeatureSCChange!,
            peakFlow: e.hydroChange!,
            reaches: e.reachesChange!,
          }
          if(newNotification.alignment === 0 &&  newNotification.peakFlow === 0 && newNotification.designControl === 0 && newNotification.reaches === 0 && newNotification.surface === 0){
            this.notificationDictionary[e.alignedElementId!] = null;
          }
          else{
            this.notificationDictionary[e.alignedElementId!] = newNotification
          }
        })
        if (response.data.validationStatusErrorCode) {
          await MessageCenter.addNotificationFromCode(response.data.validationStatusErrorCode);
        }
        if (Object.entries(this.notificationDictionary).some(([key, value]) => value !== null)) {
          this.showIcon = true;
        } else {
          this.showIcon = false;
        }
      }
    }
    catch(error){
      MessageCenter.addError((error as Error).message);
    }
  }

  @action.bound loadData = async() => {
    // Remove alignment layers when returning to Summary page
    if (this.mapView) {
      const groupLayer = this.mapView.map.findLayerById(`alignment${waterwayStore.alignmentId}`) as GroupLayer;
      if (groupLayer) this.mapView.map.remove(groupLayer);
      groupLayer?.destroy();
      const alignmentSketchLayer = this.mapView.map.findLayerById('alignment_sketch');
      if (alignmentSketchLayer) this.mapView.map.remove(alignmentSketchLayer);
      alignmentSketchLayer?.destroy();
    }
    designStore.setLoader('Fetching data...', 'Design Waterway', 0, 30);
    designStore.setLoader('Getting notifications...', 'Design Waterway', 31, 60);
    await this.getNotifications();
    designStore.setLoader('Retrieving waterway summary...', 'Design Waterway', 61, 90);
    await this.getWaterwaySummary();
    designStore.setLoader('Finalizing...', 'Design Waterway', 91, 100);
    setTimeout(() => {
      designStore.setLoader('');
      designStore.setLoader('');
      designStore.setLoader('');
      designStore.setLoader('');
    }, 2000)
  }

  @action.bound async callRebuildSurface() {
    await this.rebuildSurface();
  }

  @action.bound async rebuildSurface(fromDelete: boolean = false) {
    if (this.mapView && !fromDelete) {
      waterwayStore.waterwayLayers.forEach((layer) => {
        this.mapView!.map.remove(layer);
        layer.destroy();
      });
    }
    if (this.waterwaySummaryStationList) {
      const alignedDesignElementIds: number[] = this.waterwaySummaryStationList.filter(e => e.select).map(e => e.alignedDesignElementId).filter(e => e !== -1); // filter out undefined
      try {
        designStore.setLoader("Rebuilding surface...");
        if (alignedDesignElementIds.length > 0) {
          const payload: DesignSurfaceRequest = {
            includeContour: true,
            sessionId: stores.AppStore.designStore.projectsStore.currentSessionId,
            structureDesElemIds: alignedDesignElementIds,
            linear: true
          }
          const response = await StructureController.getMultipleDesignSurfacesUsingPOST(payload);
          if (response && response.data) {
            if (response.data.designedContours) {
              const data = response.data;
              const tin = data.designedTinTriangles ? data.designedTinTriangles : [];
              await DesignContainerVM.loadSurfaceFromData(tin, undefined, data.designedContours, undefined, undefined);
            }
            else if (!response.data.designedContours) {
              console.error("API getMultipleDesignSurfacesUsingPOST response designedContours is null")
            }
          }
        }
        else {
          await DesignContainerVM.displayOriginalSurface();
        }

      } catch (e) {
        MessageCenter.addError((e as Error).message);
      } finally {
        designStore.setLoader("");
      }
    }
  }


  @action.bound async refreshElements() {
    if (this.mapView) {
      waterwayStore.waterwayLayers.forEach((layer) => {
        this.mapView!.map.remove(layer);
        layer.destroy();
      })
    }
    if (this.waterwaySummaryStationList) {
      const selectedWaterways = this.waterwaySummaryStationList.filter(e => e.select);
      if (selectedWaterways.length === 0) {
        if (this.mapView && !designStore.hideSingleCrossSection) designStore.validateSingleCrossSection(this.mapView);
        return;
      }
      try {
        designStore.setLoader("Refreshing Elements...");
        for (const waterway of selectedWaterways) {
          const waterwayId = waterway.alignedDesignElementId;
          const response = await WaterwayDesignToolsController.getAlignedElementShapeByAlignedElementIdUsingGET(waterwayId);
          const res = await WaterwayDesignToolsController.getWaterwayInputsByAlignedElementDesignParamsIdUsingGET(waterwayId);

          //setting buffer option case 1) for refresh Elements button clicked
          if(res.data.designControlDto && res.data.designControlDto.seedingBufferOption ){
            waterwayStore.seedingBufferOption = res.data.designControlDto.seedingBufferOption;
          }

          if (response && response.data && response.data.alignedFeatureDesignShapeJson) {
            const channelResult = JSON.parse(response.data.alignedFeatureDesignShapeJson);
            if (!channelResult) {
              throw new Error("Failed to retrieve channel data from the service.");
            }
            if (this.mapView) {
                waterwayStore.addWaterwayToMap(
                    channelResult.alignedStructureShapeDTO,
                    this.mapView,
                    waterwayId.toString(), 
                    waterway.name,
                    true
                );
            }
          } else {
            console.error(`Failed to retrieve waterway data for ID ${waterwayId}`);
          }
        }
      } catch (error) {
        console.error("Error refreshing elements:", error);
        MessageCenter.addError((error as Error).message);
      } finally {
        designStore.setLoader("");
        if (this.mapView && !designStore.hideSingleCrossSection) designStore.validateSingleCrossSection(this.mapView);
      }
    }
  }


  elementActions = {
    'Edit': { action: this.editSummaryStation , disabled : false},
    'Delete': { action: this.deleteSummaryStation },
  };


   @action.bound async handleCheckboxChange(index: number) {
    const item = this.waterwaySummaryStationList[index];
    const correspondingItem = this.allWDTProjectList.find(e => e.alignedElementId === item.alignedDesignElementId);

    if (this.notificationDictionary[item.alignedDesignElementId]) {
      // If there are notifications, disable the checkbox
      item.select = false;
      return;
    }

    item.select = !item.select;

    if (correspondingItem && correspondingItem.reachesChange === 0 && correspondingItem.projectStatus) {
      // Do nothing, the checkbox stays selected
    } else if (correspondingItem) {
      await new Promise(resolve => setTimeout(resolve, 400));
      item.select = false;
      MessageCenter.addWarningFromCode('WDT_121');
    }
  }

  @action.bound editSummaryStation(e: any, item: any) {
    const id = item.alignedDesignElementId;
    this.onClickEdit();
    if (id) {
      stores.AppStore.waterwayDesignStore.alignedDesignElementId = id;
    }
  }

  @action.bound async getAllAlignmentsStatus() {
    const sessionId = designStore.projectsStore.currentSessionId
    try {
        const response = await DesignElementController.getDesignElementsBySessionAndTypeUsingGET(sessionId, "Alignment");
        if (response && response.data && response.data.designElementResponses) {
           const enableEdit = response.data.designElementResponses.some(item => item.flagUpToDate === false);
           this.elementActions.Edit.disabled = !enableEdit    
        } else {
          //add error code if any
        }
    } catch (error) {
        console.error('getAllAlignmentsStatus error:', error);
    }
}

  @action.bound async deleteSummaryStation(e: any, item: any) {
    const paramsID = item.alignedDesignElementId;
    try {
      designStore.setLoader("Deleting Waterway...");
      await WaterwayDesignToolsController.deleteWaterwayByAlignedElementDesignParamsIdUsingPOST(paramsID);
      const lyr = this.mapView?.map.findLayerById(`Waterway${paramsID}`);
      if (this.mapView && this.mapView.map && lyr) this.mapView?.map.remove(lyr);
      await this.loadData();
      await this.rebuildSurface(true);
    } catch (error) {
      MessageCenter.addError((error as Error).message);
    } finally {
      designStore.setLoader("");
      if (this.mapView && !designStore.hideSingleCrossSection) designStore.validateSingleCrossSection(this.mapView);
    }
  }
}